import { cva } from 'class-variance-authority';
import * as React from 'react';
import { createContext, use, useMemo } from 'react';

import { cn } from '@/lib/utils';

import { CARD_PADDING, CARD_VARIANT } from './types/card-variants-enums';





interface CardContextValue {
  isFoundation: boolean;
  padding: CARD_PADDING;
}

const CardContext = createContext<CardContextValue>({
  isFoundation: false,
  padding: CARD_PADDING.DEFAULT,
});

function useCardContext(): CardContextValue {
  return use(CardContext);
}

const foundationCardVariants = cva(
  'rounded-surface text-content-primary',
  {
    variants: {
      variant: {
        [CARD_VARIANT.OUTLINED]: 'border border-border-default bg-surface-base shadow-none',
        [CARD_VARIANT.ELEVATED]:
          'border border-border-subtle bg-surface-raised shadow-surface',
        [CARD_VARIANT.FLAT]: 'border-0 bg-transparent shadow-none',
      },
    },
    defaultVariants: {
      variant: CARD_VARIANT.OUTLINED,
    },
  }
);

const foundationSectionPadding = {
  [CARD_PADDING.NONE]: {
    header: 'p-0',
    content: 'p-0',
    footer: 'p-0',
  },
  [CARD_PADDING.COMPACT]: {
    header: 'p-4',
    content: 'p-4 pt-0',
    footer: 'p-4 pt-0',
  },
  [CARD_PADDING.DEFAULT]: {
    header: 'p-6',
    content: 'p-6 pt-0',
    footer: 'p-6 pt-0',
  },
  [CARD_PADDING.SPACIOUS]: {
    header: 'p-8',
    content: 'p-8 pt-0',
    footer: 'p-8 pt-0',
  },
} as const;

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CARD_VARIANT;
  padding?: CARD_PADDING;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, style, variant, padding, ...props }, ref) => {
    const isFoundation = variant !== undefined || padding !== undefined;
    const resolvedVariant = variant ?? CARD_VARIANT.OUTLINED;
    const resolvedPadding = padding ?? CARD_PADDING.DEFAULT;
    const foundationContextValue = useMemo(
      () => ({
        isFoundation: true,
        padding: resolvedPadding,
      }),
      [resolvedPadding]
    );

    if (isFoundation) {
      return (
        <CardContext value={foundationContextValue}>
          <div
            ref={ref}
            data-slot="card"
            data-variant={resolvedVariant}
            data-padding={resolvedPadding}
            className={cn(foundationCardVariants({ variant: resolvedVariant }), className)}
            {...props}
          />
        </CardContext>
      );
    }

    return (
      <div
        ref={ref}
        className={cn('rounded-lg border bg-card text-card-foreground', className)}
        style={{
          boxShadow: 'var(--shadow-card), var(--ring-subtle)',
          borderColor: 'hsl(var(--border) / var(--border-opacity))',
          ...style,
        }}
        {...props}
      />
    );
  }
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { isFoundation, padding } = useCardContext();

    return (
      <div
        ref={ref}
        data-slot={isFoundation ? 'card-header' : undefined}
        className={cn(
          'flex flex-col space-y-1.5',
          isFoundation ? foundationSectionPadding[padding].header : 'p-6',
          className
        )}
        {...props}
      />
    );
  }
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => {
    const { isFoundation } = useCardContext();

    return (
      <h3
        ref={ref}
        data-slot={isFoundation ? 'card-title' : undefined}
        className={cn(
          isFoundation
            ? 'type-section font-semibold tracking-tight text-content-primary'
            : 'text-xl font-semibold leading-none tracking-tight',
          className
        )}
        {...props}
      />
    );
  }
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { isFoundation } = useCardContext();

  return (
    <p
      ref={ref}
      data-slot={isFoundation ? 'card-description' : undefined}
      className={cn(
        isFoundation
          ? 'type-body font-normal text-content-secondary'
          : 'text-sm text-muted-foreground',
        className
      )}
      {...props}
    />
  );
});
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { isFoundation, padding } = useCardContext();

    return (
      <div
        ref={ref}
        data-slot={isFoundation ? 'card-content' : undefined}
        className={cn(
          'w-full',
          isFoundation ? foundationSectionPadding[padding].content : 'p-6 pt-0',
          className
        )}
        {...props}
      />
    );
  }
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { isFoundation, padding } = useCardContext();

    return (
      <div
        ref={ref}
        data-slot={isFoundation ? 'card-footer' : undefined}
        className={cn(
          'flex items-center',
          isFoundation ? foundationSectionPadding[padding].footer : 'p-6 pt-0',
          className
        )}
        {...props}
      />
    );
  }
);
CardFooter.displayName = 'CardFooter';

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
